import {
    getTrackingTime,
    getMonitoringProgressTrackingTimeByAdmin,
} from '@/api/trackingTime';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { TrackingTimePayloadInterface } from '@/models/trackingTime.model';
import { useMemo, useCallback } from 'react';
import { format } from 'date-fns';
import { getUsers } from '@/api/user';
import { getActivityStatistics } from '@/api/statistical/statistical';
import { batchRequests, getSuccessfulResults } from '@/utils/batchApi';
import { getPayrollByMonth, generatePayroll, updatePayrollEntry, sendPayrollEmails, sendPayrollTestEmails } from '@/api/payroll';
import type { PayrollEntry } from '@/models/payroll.model';

const useTrackingTime = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const query = useMemo(
        () => ({
            uid: searchParams.get('uid') || '',
            page: Number(searchParams.get('page')) || 1,
            limit: Number(searchParams.get('limit')) || 10,
        }),
        [searchParams]
    );

    const handleSetQuery = useCallback(
        (newQuery: TrackingTimePayloadInterface) => {
            setSearchParams({
                uid: newQuery.uid || '',
                page:
                    newQuery.page?.toString() || searchParams.get('page') || '',
                limit:
                    newQuery.limit?.toString() ||
                    searchParams.get('limit') ||
                    '',
            });
        },
        [searchParams, setSearchParams]
    );

    const { data, isLoading, error } = useQuery({
        queryKey: ['trackingTime', query],
        queryFn: () => getTrackingTime(query),
        staleTime: 30000, // 30 seconds
        refetchInterval: 60000, // Refetch every minute instead of constant polling
        refetchOnWindowFocus: false, // Disable refetch on window focus
    });

    return {
        data,
        isLoading,
        error,
        handleSetQuery,
        query: {
            uid: searchParams.get('uid') || '',
            page: Number(searchParams.get('page')) || 1,
            limit: Number(searchParams.get('limit')) || 10,
        },
    };
};

export const useDetailTracking = (uid: string) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const isMonthly = useMemo(() => {
        return searchParams.get('isMonthly') === 'true';
    }, [searchParams]);

    const date = useMemo(() => {
        return searchParams.get('date') || format(new Date(), 'yyyy-MM-dd');
    }, [searchParams]);

    const handleSetDate = useCallback(
        (newDate: string) => {
            setSearchParams({
                date: newDate,
                isMonthly: isMonthly ? 'true' : 'false',
            });
        },
        [isMonthly, setSearchParams]
    );

    const handleToggleMonthly = useCallback(
        (checked: boolean) => {
            setSearchParams({
                date: date,
                isMonthly: checked ? 'true' : 'false',
            });
        },
        [date, setSearchParams]
    );

    const { data, isLoading, error } = useQuery({
        queryKey: ['detailTracking', uid, date, isMonthly],
        queryFn: () =>
            getMonitoringProgressTrackingTimeByAdmin({
                uid,
                date,
                queryByMonth: isMonthly ? 'true' : 'false',
            }),
        enabled: Boolean(uid), // Only run query if uid exists
        staleTime: 30000, // 30 seconds
        refetchInterval: isMonthly ? false : 30000, // Only auto-refetch for daily view
        refetchOnWindowFocus: false,
    });

    return {
        data,
        isLoading,
        error,
        date,
        isMonthly,
        handleSetDate,
        handleToggleMonthly,
    };
};

export const useStatisticsTracking = (selectedMonth: Date) => {
    const monthStr = useMemo(
        () => format(selectedMonth, 'yyyy-MM'),
        [selectedMonth]
    );

    // First, get all users with caching
    const { data: usersData, isLoading: isLoadingUsers } = useQuery({
        queryKey: ['users', 'statistics'],
        queryFn: () => getUsers({ page: 1, limit: 100 }),
        staleTime: 5 * 60 * 1000, // 5 minutes - users don't change frequently
        refetchOnWindowFocus: false,
    });

    const userIds = useMemo(() => {
        return usersData?.users?.map((user) => user._id) || [];
    }, [usersData?.users]);

    // Batch API calls with better caching and error handling using the new batch utility
    const { data: statisticsData, isLoading: isLoadingStats } = useQuery({
        queryKey: ['statistics', monthStr, userIds.length], // Use length instead of full array
        queryFn: async ({ signal }) => {
            if (userIds.length === 0) return [];

            // Use the batch utility for better performance and error handling
            // Reduced concurrency to prevent request cancellations
            const results = await batchRequests(
                userIds,
                async (uid) => {
                    return getMonitoringProgressTrackingTimeByAdmin({
                        uid,
                        date: selectedMonth,
                        queryByMonth: 'true',
                    });
                },
                {
                    chunkSize: 4, // Smaller chunks to reduce load
                    delayBetweenChunks: 300, // Longer delay to be more server-friendly
                    maxConcurrency: 2, // Very low concurrency to prevent cancellations
                    signal, // Pass abort signal to handle cancellations
                }
            );

            return getSuccessfulResults(results);
        },
        enabled: userIds.length > 0,
        staleTime: 2 * 60 * 1000, // 2 minutes for statistics
        refetchOnWindowFocus: false,
        retry: 1, // Reduce retries for failed requests
        gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    });

    const { data: activityData, isLoading: isLoadingActivity } = useQuery({
        queryKey: ['activity', monthStr, userIds.length], // Use length instead of full array
        queryFn: async ({ signal }) => {
            if (userIds.length === 0) return [];

            // Use the batch utility for activity statistics
            // Reduced concurrency to prevent request cancellations
            const results = await batchRequests(
                userIds,
                async (uid) => {
                    return getActivityStatistics({
                        uid,
                        date: monthStr,
                    });
                },
                {
                    chunkSize: 4, // Smaller chunks to reduce load
                    delayBetweenChunks: 300, // Longer delay to be more server-friendly
                    maxConcurrency: 2, // Very low concurrency to prevent cancellations
                    signal, // Pass abort signal to handle cancellations
                }
            );

            return getSuccessfulResults(results);
        },
        enabled: userIds.length > 0,
        staleTime: 2 * 60 * 1000, // 2 minutes for activity data
        refetchOnWindowFocus: false,
        retry: 1,
        gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    });

    const formattedData = useMemo(() => {
        if (!statisticsData || !usersData?.users || !activityData) return [];

        return statisticsData.map((result, index) => {
            const activity = activityData[index];
            const user = usersData.users.find((u) => u._id === userIds[index]);
            return {
                key: userIds[index],
                name: user?.name || result?.user?.name || 'Unknown User',
                totalTime: result?.totalTime || 0,
                tasks: activity?.tasks?.length || 0,
                reports: activity?.reports?.length || 0,
                checkIns: activity?.uniqueCheckInDays || activity?.checkIns?.length || 0,
                wfh: activity?.wfh?.length || 0,
                lateDays: activity?.lateDays || 0,
                totalLeave: activity?.totalLeave || 0,
            };
        });
    }, [statisticsData, usersData?.users, activityData, userIds]);

    return {
        data: formattedData,
        isLoading: isLoadingUsers || isLoadingStats || isLoadingActivity,
    };
};

export const usePayroll = (selectedMonth: Date) => {
  const monthStr = useMemo(
    () => format(selectedMonth, 'yyyy-MM'),
    [selectedMonth]
  );

  const { data, isLoading, refetch } = useQuery<PayrollEntry[]>({
    queryKey: ['payroll', monthStr],
    queryFn: () => getPayrollByMonth(monthStr),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const handleGenerate = useCallback(
    async (requiredWorkingDays?: number, signal?: AbortSignal) => {
      await generatePayroll(monthStr, requiredWorkingDays, signal);
      await refetch();
    },
    [monthStr, refetch]
  );

  const handleUpdate = useCallback(
    async (id: string, payload: Partial<PayrollEntry>) => {
      await updatePayrollEntry(id, payload);
      await refetch();
    },
    [refetch]
  );

  const handleSendEmails = useCallback(async () => {
    await sendPayrollEmails(monthStr);
  }, [monthStr]);

  const handleSendTestEmails = useCallback(
    async (email: string) => {
      await sendPayrollTestEmails(monthStr, email);
    },
    [monthStr],
  );

  return {
    data: data || [],
    isLoading,
    month: monthStr,
    generate: handleGenerate,
    update: handleUpdate,
    sendEmails: handleSendEmails,
    sendTestEmails: handleSendTestEmails,
    };
};

export default useTrackingTime;